/**
 * Hook para consultar datos de un CUIT en AFIP/ARCA
 *
 * Usa useMutation (acción del usuario, no query automática).
 * Obtiene el token de Auth0 directamente al hacer la consulta.
 */

import { useMutation } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback } from 'react'
import { toast } from 'sonner'
import type { ConsultaPadronResponse } from '@/types/afip'

interface UseConsultarCuitAFIPOptions {
  onSuccess?: (data: ConsultaPadronResponse) => void
}

export function useConsultarCuitAFIP(options: UseConsultarCuitAFIPOptions = {}) {
  const { getAccessTokenSilently } = useAuth0()

  // Fetch directly with token — no module-level getter needed
  const consultarPadron = useCallback(
    async (cuit: string): Promise<ConsultaPadronResponse> => {
      const token = await getAccessTokenSilently()

      const response = await fetch('/.netlify/functions/afip-consultar-padron', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ cuit }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Error consultando padrón de AFIP')
      }

      return data as ConsultaPadronResponse
    },
    [getAccessTokenSilently]
  )

  const mutation = useMutation({
    mutationFn: consultarPadron,
    onSuccess: (data) => {
      if (data.encontrado && data.persona) {
        const nombre = data.persona.nombre
          ? `${data.persona.nombre} ${data.persona.apellido || ''}`.trim()
          : data.persona.razonSocial || 'Contribuyente'
        // ORR-388: `impuestosInscriptos` solo se llena para el Régimen General — para un
        // monotributista está vacío por construcción (afip-helpers.ts solo lo extrae de
        // datosRegimenGeneral), así que no alcanza como señal de "sin CUIT activo".
        // Probe R5 (2026-09-10, monotributista real en PROD): `estadoClave` sí viene "ACTIVO"
        // con impuestosInscriptos: [] — es el campo que ARCA expone para esto, y ya lo usa
        // StepIdentidadNew.tsx:446 para el mismo propósito.
        // Avisar cuando el CUIT no está activo, O cuando no hay ninguna otra señal de
        // inscripción (solo CUIL, el motivo original del aviso: sin impuestos del RG, sin
        // categoría de monotributo, sin actividades).
        const sinSenalDeInscripcion =
          data.persona.impuestosInscriptos.length === 0 &&
          !data.persona.categoriaMonotributo &&
          data.persona.actividades.length === 0
        const sinCuitActivo = data.persona.estadoClave !== 'ACTIVO' || sinSenalDeInscripcion

        if (sinCuitActivo) {
          toast.warning(
            `${nombre}: figura en el padrón pero sin CUIT activo (no está inscripto en AFIP/ARCA, posiblemente solo tenga CUIL). Los datos fiscales pueden quedar incompletos.`
          )
        } else {
          toast.success(`Datos obtenidos de ARCA: ${nombre}`)
        }
      } else {
        toast.info(
          'No se encontró un CUIT activo para este número. Si la persona solo tiene CUIL (todavía no está inscripta en AFIP/ARCA), no figura en el padrón.'
        )
      }

      options.onSuccess?.(data)
    },
    onError: (error: Error) => {
      console.error('Error consultando AFIP:', error)
      const mensaje = error.message.includes('Login required') || error.message.includes('consent')
        ? 'Sesión expirada. Recargá la página e intentá de nuevo.'
        : error.message.includes('Token getter')
        ? 'Error de autenticación. Recargá la página.'
        : `Error al consultar AFIP/ARCA: ${error.message}`
      toast.error(mensaje)
    },
  })

  return {
    consultarCuit: mutation.mutate,
    consultarCuitAsync: mutation.mutateAsync,
    data: mutation.data ?? null,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}
